
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WaitlistForm from '@/components/WaitlistForm';
import WaitlistSuccess from '@/components/WaitlistSuccess';
import BonusPromoCard from '@/components/BonusPromoCard';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const Waitlist = () => {
  const [searchParams] = useSearchParams();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessingGoogle, setIsProcessingGoogle] = useState(false);

  useEffect(() => {
    // Check if coming from auth form success
    const successParam = searchParams.get('success');
    if (successParam === 'true') {
      setShowSuccess(true);
      return;
    }

    const handleGoogleCallback = async () => {
      const isGoogleSignup = searchParams.get('google_signup');
      
      if (isGoogleSignup) {
        setIsProcessingGoogle(true);
        console.log('Processing Google waitlist signup');
        
        try {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            toast.error('Google sign-up failed. Please try again.');
            return;
          }

          if (session?.user) {
            const user = session.user;
            console.log('Google user data:', user);
            
            // First, check the user's waitlist status
            const { data: waitlistStatus, error: statusError } = await supabase
              .rpc('get_waitlist_status', { user_email: user.email! });
            
            if (statusError) {
              console.error('Error checking waitlist status:', statusError);
              toast.error('Failed to check waitlist status. Please try again.');
              return;
            }
            
            console.log('Waitlist status for', user.email, ':', waitlistStatus);
            
            // Handle different waitlist statuses
            if (waitlistStatus === 'approved') {
              // User is approved - keep them signed in and redirect to dashboard
              toast.success("Welcome back! Redirecting to dashboard...");
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 1500);
              return;
            } else if (waitlistStatus === 'pending') {
              // User is already on waitlist but not approved yet
              toast.error("You're already on the waitlist! Please wait for approval.");
              await supabase.auth.signOut();
              setShowSuccess(true);
              return;
            } else if (waitlistStatus === 'rejected') {
              // User application was rejected
              toast.error("Your waitlist application was not approved.");
              await supabase.auth.signOut();
              setShowSuccess(true);
              return;
            }
            
            // If waitlistStatus is 'not_found', add user to waitlist (original behavior)
            const { error: insertError } = await supabase
              .from('waitlist')
              .insert([
                {
                  email: user.email!,
                  signup_method: 'google',
                  google_user_data: {
                    id: user.id,
                    name: user.user_metadata?.full_name || user.user_metadata?.name,
                    avatar_url: user.user_metadata?.avatar_url,
                    provider: user.app_metadata?.provider
                  }
                }
              ]);

            if (insertError) {
              console.error('Error adding to waitlist:', insertError);
              toast.error('Failed to join waitlist. Please try again.');
            } else {
              toast.success("Successfully joined the waitlist with Google!");
            }
            
            // Sign out the user since we don't want them authenticated yet
            await supabase.auth.signOut();
            setShowSuccess(true);
          }
        } catch (error) {
          console.error('Error processing Google signup:', error);
          toast.error('Failed to join waitlist. Please try again.');
        } finally {
          setIsProcessingGoogle(false);
          // Clean up URL
          window.history.replaceState({}, '', '/waitlist');
        }
      }
    };

    handleGoogleCallback();
  }, [searchParams]);

  const handleSuccess = () => {
    setShowSuccess(true);
  };

  if (showSuccess) {
    return <WaitlistSuccess />;
  }

  if (isProcessingGoogle) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <img src="/coin_logo.png" alt="AI Wealth Manager" className="h-24 w-24 mb-4 mx-auto animate-spin" />
          <p className="text-white">Processing your Google sign-up...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-1/4 w-96 h-96 bg-[#9E7C47] rounded-full filter blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 bg-[#F9F1A9] rounded-full filter blur-[120px] animate-pulse-slow"></div>
      </div>
      
      <div className="w-full max-w-md text-center relative z-10">
        <div className="mb-8 flex flex-col items-center">
          <img src="/coin_logo.png" alt="AI Wealth Manager" className="h-24 w-24 mb-4" />
          <h1 className="text-3xl font-bold mb-2 text-white">Join the Waitlist</h1>
          <p className="text-white/70">Be among the first to experience AI-powered wealth management</p>
        </div>
        
        <BonusPromoCard />
        
        <WaitlistForm onSuccess={handleSuccess} />
        
        <div className="mt-8">
          <a href="/" className="text-white/50 hover:text-white text-sm">
            ← Back to home
          </a>
        </div>
      </div>
    </div>
  );
};

export default Waitlist;
